import { useCallback, useEffect, useRef } from "react";

/**
 * Throttles pushed items and flushes them as a single batch on a short timer, so
 * a burst of events triggers one `onFlush` call (and one re-render) instead of
 * one per item. Returns a stable `push` function. The latest `onFlush` is
 * always used, so `push` keeps a stable identity even when `onFlush` changes.
 */
export const useThrottledFlush = <T>(onFlush: (items: T[]) => void, delayMs = 250) => {
  const pendingRef = useRef<T[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onFlushRef = useRef(onFlush);
  useEffect(() => {
    onFlushRef.current = onFlush;
  });

  const flush = useCallback(() => {
    timerRef.current = null;
    if (pendingRef.current.length === 0) {
      return;
    }
    const pending = pendingRef.current;
    pendingRef.current = [];
    onFlushRef.current(pending);
  }, []);

  const push = useCallback(
    (item: T) => {
      pendingRef.current.push(item);
      if (timerRef.current === null) {
        timerRef.current = setTimeout(flush, delayMs);
      }
    },
    [flush, delayMs]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return push;
};
